import { history, historyKeymap } from "@codemirror/commands";
import { closeSearchPanel, openSearchPanel } from "@codemirror/search";
import {
  EditorSelection,
  EditorState,
  type Extension,
} from "@codemirror/state";
import {
  EditorView,
  keymap,
  lineNumbers,
  type ViewUpdate,
} from "@codemirror/view";
import {
  forwardRef,
  useEffect,
  useEffectEvent,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import baseExtensions from "./extensions";
import { type DiffRange, diffExtension } from "./extensions/diff";
import { getLanguageExtension } from "./extensions/language";
import { type PinnedRange, pinnedExtension } from "./extensions/pinned";
import { searchExtension } from "./extensions/search";
import { type CodeSelection, selectionExtension } from "./extensions/selection";
import { SelectionTooltip } from "./SelectionTooltip";
import { useCodeEditorSearchContext } from "./useCodeEditorSearch";
import { useCodeMirror } from "./useCodeMirror";

export type { CodeSelection, DiffRange, PinnedRange };

export type CodeEditorHandle = {
  scrollToLine: (line: number) => void;
  revealLine: (line: number) => void;
  openSearch: () => void;
  closeSearch: () => void;
  getView: () => EditorView | null;
  scrollToBottom: () => void;
  isAtBottom: (thresholdPx?: number) => boolean;
};

type Rect = { top: number; left: number; width: number; height: number };

function findScroller(view: EditorView | null): HTMLElement | null {
  if (!view) return null;
  let el: HTMLElement | null = view.dom;
  while (el) {
    const overflowY = getComputedStyle(el).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return el;
    el = el.parentElement;
  }
  return null;
}

function computeRect(view: EditorView): Rect | null {
  const sel = view.state.selection.main;
  if (sel.empty) return null;
  // Clicking at the visual top of a line resolves to the previous line's end
  // (just before the newline). Bump past the newline so the tooltip lands on
  // the line where the highlight visually starts, not one line above.
  let pos = sel.head;
  const line = view.state.doc.lineAt(pos);
  if (pos === line.to && line.to < view.state.doc.length) pos += 1;
  const head = view.coordsAtPos(pos);
  if (!head) return null;
  const editor = view.dom.getBoundingClientRect();
  return {
    top: head.top - editor.top,
    left: head.left - editor.left,
    width: Math.max(0, head.right - head.left),
    height: Math.max(0, head.bottom - head.top),
  };
}

type Props = {
  value: string;
  filePath: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  showLineNumbers?: boolean;
  onSelectionChange?: (selections: CodeSelection[]) => void;
  pinnedRanges?: readonly PinnedRange[];
  diffRanges?: DiffRange[];
  lineNumberMap?: readonly number[];
  onVisibleLineChange?: (line: number | null) => void;
};

export const CodeEditor = forwardRef<CodeEditorHandle, Props>(
  function CodeEditor(
    {
      value,
      filePath,
      onChange,
      readOnly = true,
      showLineNumbers = true,
      onSelectionChange,
      pinnedRanges,
      diffRanges,
      lineNumberMap,
      onVisibleLineChange,
    },
    ref
  ) {
    const isAutoScrollingRef = useRef(false);
    const searchRegistration = useCodeEditorSearchContext();
    // Mirror the latest props for CodeMirror listeners, whose stable identity
    // must not change with every prop update; written after commit so nothing
    // reads a ref during render. Must stay declared above useCodeMirror: this
    // effect has to run before that hook's doc-sync effect so the changeListener
    // echo guard (next !== valueRef.current) sees the new value and suppresses a
    // spurious onChange when the parent drives value. Moving it below reorders
    // the effects and every parent-driven update fires onChange.
    const valueRef = useRef(value);
    const onChangeRef = useRef(onChange);
    useEffect(() => {
      valueRef.current = value;
      onChangeRef.current = onChange;
    });
    const autoScrollClearTimeoutRef = useRef<ReturnType<
      typeof setTimeout
    > | null>(null);
    const liveSelectionRef = useRef<CodeSelection[]>([]);
    const selectingRef = useRef(false);
    const [rect, setRect] = useState<Rect | null>(null);
    const [editorEl, setEditorEl] = useState<HTMLElement | null>(null);

    // Hide the tooltip while the user is actively dragging, and on selection
    // clear. Reposition on geometry changes when the selection is stable.
    const [onTooltipUpdate] = useState(() => (update: ViewUpdate) => {
      if (update.state.selection.main.empty) {
        setRect(null);
        return;
      }
      if (!update.geometryChanged && !update.viewportChanged) return;
      if (selectingRef.current) return;
      setRect((prev) => (prev ? computeRect(update.view) : null));
    });
    const [tooltipListener] = useState(() =>
      EditorView.updateListener.of(onTooltipUpdate)
    );

    const [searchExt] = useState(() => searchExtension());
    const [onDocUpdate] = useState(() => (update: ViewUpdate) => {
      if (!update.docChanged) return;
      const next = update.state.doc.toString();
      if (next !== valueRef.current) onChangeRef.current?.(next);
    });
    const [changeListener] = useState(() =>
      EditorView.updateListener.of(onDocUpdate)
    );
    const hasOnChange = onChange !== undefined;
    const [recordSelections] = useState(() => (selections: CodeSelection[]) => {
      liveSelectionRef.current = selections;
    });

    const extensions = ((): Extension[] => {
      const result: Extension[] = [
        ...baseExtensions,
        searchExt,
        tooltipListener,
        EditorView.editable.of(!readOnly),
        EditorState.readOnly.of(readOnly),
        EditorView.editorAttributes.of({
          "data-readonly": String(readOnly),
        }),
      ];

      if (!readOnly) {
        result.push(history(), keymap.of(historyKeymap));
        if (hasOnChange) result.push(changeListener);
      }

      if (showLineNumbers) {
        result.push(
          lineNumbers(
            lineNumberMap
              ? {
                  formatNumber: (n) => String(lineNumberMap[n - 1] ?? n),
                }
              : undefined
          )
        );
      }

      const languageExtension = getLanguageExtension(filePath);
      if (languageExtension) {
        result.push(languageExtension);
      }

      if (onSelectionChange) {
        result.push(selectionExtension(filePath, recordSelections));
      }

      if (pinnedRanges?.length) {
        result.push(pinnedExtension(pinnedRanges));
      }

      if (diffRanges?.length) {
        result.push(diffExtension(diffRanges));
      }

      return result;
    })();

    const containerRef = useRef<HTMLDivElement>(null);
    const viewRef = useCodeMirror(containerRef, value, extensions);

    const handle = useMemo<CodeEditorHandle>(
      () => ({
        scrollToLine: (line) => {
          const view = viewRef.current;
          if (!view) return;
          const total = view.state.doc.lines;
          if (total === 0) return;
          const clamped = Math.max(1, Math.min(line, total));
          const pos = view.state.doc.line(clamped).from;
          // lineBlockAt uses CodeMirror's height map and works for any
          // position in the document (unlike coordsAtPos, which returns null
          // for positions outside the rendered viewport).
          const block = view.lineBlockAt(pos);
          const scroller = findScroller(view);
          if (!scroller) return;
          const contentRect = view.contentDOM.getBoundingClientRect();
          const scrollerRect = scroller.getBoundingClientRect();
          const contentTopWithin =
            contentRect.top - scrollerRect.top + scroller.scrollTop;
          const lineTopWithin = contentTopWithin + block.top;
          // Anchor near the top with a quarter-viewport offset so the hunk
          // and its trailing context are both visible — matches GitHub/VS Code
          // "next change" navigation.
          const offset = Math.min(120, scroller.clientHeight / 4);
          const targetTop = lineTopWithin - offset;
          isAutoScrollingRef.current = true;
          if (autoScrollClearTimeoutRef.current) {
            clearTimeout(autoScrollClearTimeoutRef.current);
          }
          autoScrollClearTimeoutRef.current = setTimeout(() => {
            isAutoScrollingRef.current = false;
            autoScrollClearTimeoutRef.current = null;
          }, 1500);
          scroller.scrollTo({
            top: Math.max(0, targetTop),
            behavior: "smooth",
          });
        },
        revealLine: (line) => {
          const view = viewRef.current;
          if (!view) return;
          const total = view.state.doc.lines;
          if (total === 0) return;
          const clamped = Math.max(1, Math.min(line, total));
          const pos = view.state.doc.line(clamped).from;
          view.dispatch({
            effects: EditorView.scrollIntoView(pos, { y: "center" }),
          });
        },
        openSearch: () => {
          const view = viewRef.current;
          if (!view) return;
          openSearchPanel(view);
        },
        closeSearch: () => {
          const view = viewRef.current;
          if (!view) return;
          closeSearchPanel(view);
        },
        getView: () => viewRef.current,
        scrollToBottom: () => {
          const scroller = findScroller(viewRef.current);
          if (!scroller) return;
          scroller.scrollTop = scroller.scrollHeight;
        },
        isAtBottom: (thresholdPx = 16) => {
          const scroller = findScroller(viewRef.current);
          if (!scroller) return true;
          const distance =
            scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
          return distance <= thresholdPx;
        },
      }),
      [viewRef]
    );

    useImperativeHandle(ref, () => handle, [handle]);

    useEffect(() => {
      if (!searchRegistration) return;
      searchRegistration.registerEditor(handle);
      return () => searchRegistration.registerEditor(null);
    }, [searchRegistration, handle]);

    useEffect(() => {
      if (!onVisibleLineChange) return;
      const view = viewRef.current;
      if (!view) return;
      const scroller = findScroller(view);
      if (!scroller) return;

      let rafId: number | null = null;

      const compute = () => {
        rafId = null;
        if (isAutoScrollingRef.current) return;
        const v = viewRef.current;
        if (!v) return;
        const editorRect = v.dom.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();
        const visibleTop = Math.max(editorRect.top, scrollerRect.top);
        const visibleBottom = Math.min(editorRect.bottom, scrollerRect.bottom);
        if (visibleBottom <= visibleTop) {
          onVisibleLineChange(null);
          return;
        }
        const centerY = (visibleTop + visibleBottom) / 2;
        const x = v.contentDOM.getBoundingClientRect().left + 1;
        const pos = v.posAtCoords({ x, y: centerY });
        if (pos == null) {
          onVisibleLineChange(null);
          return;
        }
        onVisibleLineChange(v.state.doc.lineAt(pos).number);
      };

      const onScroll = () => {
        if (rafId != null) return;
        rafId = requestAnimationFrame(compute);
      };

      const onScrollEnd = () => {
        isAutoScrollingRef.current = false;
        if (autoScrollClearTimeoutRef.current) {
          clearTimeout(autoScrollClearTimeoutRef.current);
          autoScrollClearTimeoutRef.current = null;
        }
      };

      scroller.addEventListener("scroll", onScroll, { passive: true });
      scroller.addEventListener("scrollend", onScrollEnd);

      return () => {
        scroller.removeEventListener("scroll", onScroll);
        scroller.removeEventListener("scrollend", onScrollEnd);
        if (rafId != null) cancelAnimationFrame(rafId);
      };
    }, [onVisibleLineChange, viewRef]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      const onDown = (e: MouseEvent) => {
        if ((e.target as HTMLElement | null)?.closest(".cm-panel")) return;
        selectingRef.current = true;
        setRect(null);
        viewRef.current?.focus();
      };
      const onWindowDown = (e: MouseEvent) => {
        if (!container.contains(e.target as Node)) setRect(null);
      };
      const onUp = () => {
        if (!selectingRef.current) return;
        selectingRef.current = false;
        const view = viewRef.current;
        if (!view) return;
        setRect(computeRect(view));
      };
      container.addEventListener("mousedown", onDown);
      window.addEventListener("mousedown", onWindowDown);
      window.addEventListener("mouseup", onUp);
      return () => {
        container.removeEventListener("mousedown", onDown);
        window.removeEventListener("mousedown", onWindowDown);
        window.removeEventListener("mouseup", onUp);
      };
    }, [viewRef]);

    useEffect(() => {
      setEditorEl(viewRef.current?.dom ?? null);
    }, [viewRef]);

    const commitSelection = () => {
      if (!onSelectionChange) return;
      const view = viewRef.current;
      if (!view) return;
      if (view.state.selection.main.empty) return;
      const selections = liveSelectionRef.current;
      if (!selections.length) return;
      onSelectionChange(selections);
      view.dispatch({
        selection: EditorSelection.cursor(view.state.selection.main.head),
      });
      window.getSelection()?.removeAllRanges();
    };

    const onEnter = useEffectEvent(() => commitSelection());
    useEffect(() => {
      if (!onSelectionChange) return;
      const handler = (e: KeyboardEvent) => {
        if (e.key !== "Enter") return;
        const view = viewRef.current;
        if (!view) return;
        if (view.state.selection.main.empty) return;
        const target = e.target as HTMLElement | null;
        if (target) {
          if (
            target.isContentEditable ||
            target instanceof HTMLInputElement ||
            target instanceof HTMLTextAreaElement
          ) {
            return;
          }
        }
        if (!liveSelectionRef.current.length) return;
        e.preventDefault();
        e.stopPropagation();
        onEnter();
      };
      window.addEventListener("keydown", handler, true);
      return () => window.removeEventListener("keydown", handler, true);
    }, [onSelectionChange, viewRef]);

    return (
      <>
        <div ref={containerRef} style={{ display: "contents" }} />
        {onSelectionChange && (
          <SelectionTooltip
            rect={rect}
            editorEl={editorEl}
            onClick={commitSelection}
          />
        )}
      </>
    );
  }
);
